import { useCallback } from 'react';
import { Button, DrawerSection, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { notifyUcpOpened } from '../../../utils/toastNotify.js';
import { DetailField } from '../shared/index.js';
import { ExternalLink } from 'lucide-react';

/**
 * Situation block for HTL / Client Handoff — aligned to aims-htl EVT-021: narrative
 * situation, metadata grid (workflow / entity / record / source), and a handoff
 * justification callout. Client handoff may add "View full UCP profile" (mock CT-4867).
 */
function HandoffSituationBlock({ detail, situation }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const ucpProfileUrl = detail.ucpProfileUrl;
  const justification = detail.handoffJustification ?? detail.handoffReason;

  const handleViewUcp = useCallback(() => {
    notifyUcpOpened(toast, t, ucpProfileUrl);
  }, [toast, t, ucpProfileUrl]);

  const rows = [
    { label: t('continuation.workflow'), value: detail.workflow },
    { label: t('handoff.entity'), value: detail.entity ?? detail.entityContext },
    { label: t('handoff.recordId'), value: detail.recordId },
    { label: t('handoff.source'), value: detail.source },
  ].filter((row) => row.value);

  return (
    <DrawerSection
      title={t('detail.situation')}
      rightContent={
        ucpProfileUrl ? (
          <Button variant="ghost" size="sm" rightIcon={ExternalLink} onClick={handleViewUcp}>
            {t('continuation.viewUcp')}
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-4">
        {situation ? (
          <p className="text-sm leading-relaxed text-content-secondary">{situation}</p>
        ) : null}

        {rows.length > 0 ? (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {rows.map((row) => (
              <DetailField key={row.label} label={row.label} value={row.value} />
            ))}
          </dl>
        ) : null}

        {justification ? (
          <div className="rounded-lg border border-edge bg-surface-muted px-4 py-3 text-sm leading-relaxed text-content-secondary">
            {justification}
          </div>
        ) : null}
      </div>
    </DrawerSection>
  );
}

export default HandoffSituationBlock;
